import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Stack,
  Card,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import DownloadIcon  from "@mui/icons-material/Download";
import WarningIcon   from "@mui/icons-material/Warning";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import TableChartIcon from "@mui/icons-material/TableChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SortIcon from "@mui/icons-material/Sort";
import CategoryIcon from "@mui/icons-material/Category";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import ArchitectureIcon from "@mui/icons-material/Architecture";

import { useParams, Outlet, useLocation } from "react-router-dom";
import api from "../../utils/api";

import {
  fetchArchitecturalRequests,
  fetchStatuses,
  fetchRequestTypes,
} from "./Utils/archRequest.api";
import { getStatusInfo } from "./Utils/archRequest.utils";
import StatusFilterCards from "./StatusFilterCards";
import ArchitecturalRequestCard from "./ArchitecturalRequestsCards";
import ArchitecturalRequestTable from "./ArchitecturalRequestsTable";

/* ------------------------------------------------------------------ */
/* Shared styles                                                       */
/* ------------------------------------------------------------------ */
const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  px: 2,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "#2f63ad",
    color: "#2f63ad",
    backgroundColor: "rgba(47,99,173,0.04)",
  },
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.96)",
  },
};

/* ------------------------------------------------------------------ */
/* Filter Bar                                                          */
/* ------------------------------------------------------------------ */
const ArchRequestFilterBar = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  filter,
  setFilter,
  sortOrder,
  setSortOrder,
  selectedType,
  setSelectedType,
  requestTypes,
  onReset,
  totalCount,
}) => {
  const hasActive =
    !!searchTerm || !!filter.startDate || !!filter.endDate || !!selectedType;

  const typeLabel =
    selectedType &&
    (requestTypes.find((t) => String(t.id) === String(selectedType))?.type ||
      selectedType);

  const dateLabel =
    filter.startDate || filter.endDate
      ? `${filter.startDate || "…"}. ${filter.endDate || "…"}`
      : "";

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 3 }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2.25,
          pb: 1.5,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <ArchitectureIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Architectural Requests
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Search, filter, and review architectural requests across the community.
            </Typography>
          </Box>

          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid rgba(47,99,173,0.08)",
              "& .MuiToggleButton-root": {
                px: 1.75,
                border: "none",
                textTransform: "none",
                fontWeight: 600,
                color: "#5f6b7a",
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
            }}
          >
            <ToggleButton value="cards">
              <ViewModuleIcon sx={{ mr: 1 }} /> Cards
            </ToggleButton>
            <ToggleButton value="table">
              <TableChartIcon sx={{ mr: 1 }} /> Table
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              placeholder="Search by request ID, property, type, or status..."
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start"
              size="small"
              value={filter.startDate}
              onChange={(e) =>
                setFilter((f) => ({ ...f, startDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End"
              size="small"
              value={filter.endDate}
              onChange={(e) =>
                setFilter((f) => ({ ...f, endDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Sort by"
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={filterFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="newest">Newest to Oldest</MenuItem>
              <MenuItem value="oldest">Oldest to Newest</MenuItem>
              <MenuItem value="idAsc">Request ID Ascending</MenuItem>
              <MenuItem value="idDesc">Request ID Descending</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Request Type"
              size="small"
              value={selectedType}
              onChange={(e) => setSelectedType(String(e.target.value))}
              sx={filterFieldSx}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {requestTypes
                .slice()
                .sort((a, b) => (a.type || "").localeCompare(b.type || ""))
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.type}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
                mt: 0.5,
              }}
            >
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {searchTerm && (
                  <Chip
                    label={`Search: ${searchTerm}`}
                    onDelete={() => setSearchTerm("")}
                    size="small"
                    variant="outlined"
                  />
                )}
                {dateLabel && (
                  <Chip
                    label={`Date: ${dateLabel}`}
                    onDelete={() => setFilter({ startDate: "", endDate: "" })}
                    size="small"
                    variant="outlined"
                  />
                )}
                {selectedType && (
                  <Chip
                    label={`Type: ${typeLabel}`}
                    onDelete={() => setSelectedType("")}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  {totalCount} result{totalCount === 1 ? "" : "s"}
                </Typography>

                {hasActive && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ClearRoundedIcon />}
                    onClick={onReset}
                    sx={secondaryButtonSx}
                  >
                    Reset
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Main Page                                                           */
/* ------------------------------------------------------------------ */
const ArchitecturalRequestPage = () => {
  const { id } = useParams();
  const location = useLocation();

  const [requests, setRequests] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filter, setFilter] = useState({ startDate: "", endDate: "" });
  const [viewMode, setViewMode] = useState("table");
  const [sortOrder, setSortOrder] = useState("newest");
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [slaBreached, setSlaBreached] = useState(0);
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      const params = new URLSearchParams();
      if (filterStatuses.length === 1) params.set("status", filterStatuses[0]);
      if (selectedType) params.set("type_id", selectedType);
      const res = await fetch(`${window.location.origin}/api/architectural-requests/export/csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `ar-export-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error("Export failed:", err); }
    finally { setExporting(false); }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location?.state?.reload) {
      loadData();
      window.history.replaceState({}, document.title);
    }
  }, [location?.state?.reload]);

  // Load SLA breach count (must be before any conditional returns)
  useEffect(() => {
    api.get("/architectural-requests/sla-status").then(r => {
      const breached = (r.data || []).filter(x => x.sla_status === "breached").length;
      setSlaBreached(breached);
    }).catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [reqRes, statusRes, typesRes] = await Promise.all([
        fetchArchitecturalRequests(),
        fetchStatuses(),
        fetchRequestTypes(),
      ]);

      const data = reqRes.data || [];
      setRequests(data);
      setStatuses(statusRes.data || []);
      setRequestTypes(typesRes || []);

      const counts = {};
      for (const r of data) {
        const s = String(r.status || "").toLowerCase();
        counts[s] = (counts[s] || 0) + 1;
      }
      setStatusCounts(counts);
    } catch (err) {
      console.error("Failed to load architectural request data:", err);
      setFetchError("Failed to load architectural requests. Please refresh.");
      setRequests([]);
      setStatuses([]);
      setRequestTypes([]);
      setStatusCounts({});
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatuses([]);
    setFilter({ startDate: "", endDate: "" });
    setSortOrder("newest");
    setSelectedType("");
  };

  const getRequestTypeName = (typeId) => {
    const found = requestTypes.find((t) => Number(t.id) === Number(typeId));
    return found?.type || "Unknown Type";
  };

  const filtered = useMemo(() => {
    let results = [...requests];

    const terms = searchTerm
      .toLowerCase()
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (terms.length) {
      results = results.filter((r) => {
        const typeName = getRequestTypeName(r.request_type);
        const combined =
          `${r.id} ${r.property_id} ${typeName} ${r.status}`.toLowerCase();
        return terms.some((term) => combined.includes(term));
      });
    }

    if (filterStatuses.length) {
      results = results.filter((r) =>
        filterStatuses.includes(String(r.status || "").toLowerCase())
      );
    }

    if (filter.startDate || filter.endDate) {
      results = results.filter((r) => {
        const requestDate = String(r.requested_at || "").slice(0, 10);
        return (
          (!filter.startDate || requestDate >= filter.startDate) &&
          (!filter.endDate || requestDate <= filter.endDate)
        );
      });
    }

    if (selectedType) {
      results = results.filter(
        (r) => String(r.request_type) === String(selectedType)
      );
    }

    if (sortOrder === "newest") {
      results.sort((a, b) => new Date(b.requested_at) - new Date(a.requested_at));
    } else if (sortOrder === "oldest") {
      results.sort((a, b) => new Date(a.requested_at) - new Date(b.requested_at));
    } else if (sortOrder === "idAsc") {
      results.sort((a, b) => a.id - b.id);
    } else if (sortOrder === "idDesc") {
      results.sort((a, b) => b.id - a.id);
    }

    return results;
  }, [requests, searchTerm, filterStatuses, filter, sortOrder, selectedType, requestTypes]);

  const handleStatusFilter = (status) => {
    setFilterStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  if (id) return <Outlet />;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: "1200px", mx: "auto" }}>
      {fetchError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{fetchError}</Alert>}

      {/* SLA breach banner */}
      {slaBreached > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb:2, borderRadius:"12px" }}
          action={<Button size="small" color="inherit" onClick={() => {}}>View</Button>}>
          <strong>{slaBreached} request{slaBreached!==1?"s":""}</strong> have exceeded their SLA target. Review and take action.
        </Alert>
      )}

      {/* Export button row */}
      <Box sx={{ display:"flex", justifyContent:"flex-end", mb:1.5, gap:1 }}>
        <Tooltip title="Export current view to CSV">
          <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />}
            onClick={handleExportCSV} disabled={exporting}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600, borderColor:"rgba(47,99,173,0.3)", color:"#2f63ad" }}>
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </Tooltip>
      </Box>

      <ArchRequestFilterBar
        viewMode={viewMode}
        setViewMode={setViewMode}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filter={filter}
        setFilter={setFilter}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        requestTypes={requestTypes}
        onReset={handleResetFilters}
        totalCount={filtered.length}
      />

      <Box sx={{ mb: 3 }}>
        <StatusFilterCards
          statuses={statuses}
          statusCounts={statusCounts}
          activeFilters={filterStatuses}
          onToggle={handleStatusFilter}
          getStatusInfo={getStatusInfo}
        />
      </Box>

      {viewMode === "cards" ? (
        <Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Card View
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              {loading
                ? "Loading architectural requests..."
                : `Showing ${filtered.length} architectural request${
                    filtered.length === 1 ? "" : "s"
                  }.`}
            </Typography>
          </Box>

          <ArchitecturalRequestCard
            requests={filtered}
            getStatusDetails={(status) => getStatusInfo(statuses, status)}
          />
        </Box>
      ) : (
        <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
          <Box
            sx={{
              px: { xs: 1.5, sm: 2, md: 3 },
              py: 2,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Table View
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              {loading
                ? "Loading architectural requests..."
                : `Showing ${filtered.length} architectural request${
                    filtered.length === 1 ? "" : "s"
                  }.`}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Box sx={{ overflowX: "auto" }}>
              <ArchitecturalRequestTable
                rows={filtered}
                getStatusDetails={(status) => getStatusInfo(statuses, status)}
                getRequestTypeName={getRequestTypeName}
              />
            </Box>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default ArchitecturalRequestPage;